import Badge from 'atomic/badge';
import Link from 'atomic/link';
import NumberOfOffer from 'atomic/number-of-offer';
import Price from 'atomic/price';
import Rating from 'atomic/rating';
import ProductListImpression from 'components/impressions/product-impression/list/product-list-impression';
import { approximateNumber } from 'helpers/approximate-number';
import React, { useState } from 'react';
import { InView } from 'react-intersection-observer';
import WidgetProductSliderProps from '../widget-product-slider.props';
import WidgetProductSliderDesktopBasic from './components/widget-product-slider-desktop-basic';
import AlertDiscountPriceInfo from 'modules/shared/components/alert-discount-price-info';
import WidgetAttributesTable from 'modules/shared/widgets/shared/widget-attributes-table';

import './widget-product-slider.desktop.scss';

const WidgetProductSliderDesktop: React.FC<WidgetProductSliderProps> = ({ config, data }) => {
    const [WidgetProductSliderComponent, setWidgetProductSliderComponent] = useState<React.ComponentType<any>>(() => WidgetProductSliderDesktopBasic);
    const { items, count, category } = data;

    config.lazyLoad = config.lazyLoad ?? true;
    config.autoPlay = config.autoPlay ?? false;
    config.itemsOnScreen = config.itemsOnScreen ?? 5;

    const onView = (inView: boolean): void => {
        if (inView) {
            import('react-multi-carousel').then(mod => {
                setWidgetProductSliderComponent(() => mod.default);
            });
        }
    };

    const responsive = {
        desktop: {
            breakpoint: { max: 5000, min: 1024 },
            items: config.itemsOnScreen
        }
    };

    return (
        <div className="w-product-slider-d">
            <InView triggerOnce={false} as="div" onChange={onView}>
                <WidgetProductSliderComponent
                    responsive={responsive}
                    swipeable={false}
                    draggable={false}
                    slidesToSlide={config.itemsOnScreen}
                    renderButtonGroupOutside={true}
                    arrows={true}
                    autoPlay={config.autoPlay}
                    className="w-product-slider-d__list"
                    itemClass={`w-product-slider-d__list__item w-${config.itemsOnScreen}`}
                >
                    {
                        items.map(product => (
                            <Link href={product.url} key={product.id} title={product.name}
                                className="w-product-slider-d__list__item-content"
                            >
                                <ProductListImpression
                                    trackedData={{ ...product, price: product.minPrice }}
                                    className={`w-product-slider-d__list__item-content__image h-${config.itemsOnScreen}`}
                                >
                                    <img
                                        src={product.photo}
                                        alt={product.name}
                                        title={product.name}
                                        loading={config.lazyLoad ? 'lazy' : 'eager'}
                                        className={`h-${config.itemsOnScreen}`}
                                    />
                                    <div className="w-product-slider-d__list__item-content__image__badges">
                                        {product.discount > 0 && (
                                            <Badge color="red">
                                                <span>{product.discount}%</span>
                                            </Badge>
                                        )}
                                    </div>
                                </ProductListImpression>
                                <div className="w-product-slider-d__list__item-content__title">
                                    <h3>
                                        {product.name}
                                    </h3>
                                </div>
                                <div className="w-product-slider-d__list__item-content__stars">
                                    <Rating size="sm" opinions={product.opinions} />
                                </div>
                                <div className="w-product-slider-d__list__item-content__price">
                                    <Price price={product.minPrice} weight={700} />
                                    {product.discount > 0 && (
                                        <Price
                                            className="discount"
                                            price={product.minPrice + (product.minPrice * (Math.abs(product.discount) / (100 - Math.abs(product.discount))))}
                                            crossed={true}
                                            color="red"
                                            weight={500}
                                        />
                                    )}
                                </div>
                                {product.discount > 0 && (
                                    <div className="w-product-slider-d__list__item-content__discount-info">
                                        <AlertDiscountPriceInfo paddingTop={0} paddingBottom={6}/>
                                    </div>
                                )}
                                {product.discount === 0 && product.attributes && (
                                    <div className="w-product-slider-d__list__item-content__attributes">
                                        <WidgetAttributesTable attributes={product.attributes} rowsNumber={3}/>
                                    </div>
                                )}
                                <div className="w-product-slider-d__list__item-content__number-of-offer">
                                    <NumberOfOffer offers={product.offers} />
                                </div>
                            </Link>
                        ))
                    }
                </WidgetProductSliderComponent>
            </InView>
            {category && (
                <div className="w-product-slider-d__footer">
                    <Link href={category.url}
                        type="button"
                        size="lg"
                        color="transparent-secondary"
                        weight="500"
                    >
                        Sprawdź wszystkie ({approximateNumber(count)})
                    </Link>
                </div>
            )}
        </div>
    );
};

export default WidgetProductSliderDesktop;
